import React, { useState, useEffect } from "react";
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../../lib/config/api-endpoints';

interface WaitlistEntry {
  id: number;
  email: string;
  name?: string;
  software_type: string;
  interest_level: string;
  message?: string;
  created_at: string;
  notified: boolean;
}

interface WaitlistSummary {
  total_signups: number;
  by_software: Array<{
    software_type: string;
    name: string;
    count: number;
    recent_signups_24h: number;
  }>;
  recent_signups: WaitlistEntry[];
}

interface WaitlistData {
  summary: WaitlistSummary;
  entries: Record<string, WaitlistEntry[]>;
  all_entries: WaitlistEntry[];
}

const AdminSoftwareWaitlist: React.FC = () => {
  const [data, setData] = useState<WaitlistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSoftware, setSelectedSoftware] = useState<string>("all");

  useEffect(() => {
    fetchWaitlistData();
  }, []);

  const fetchWaitlistData = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.admin.softwareWaitlist, {
        headers: getAuthHeaders(API_ENDPOINTS.admin.softwareWaitlist),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.softwareWaitlist) ? 'include' : 'same-origin',
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || "Failed to fetch waitlist data");
      }
    } catch (err) {
      setError("Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSoftwareIcon = (type: string) => {
    switch (type) {
      case "cyclechef":
        return "🍽️";
      case "htma":
        return "🔬";
      case "practice_management":
        return "👩‍⚕️";
      default:
        return "💻";
    }
  };

  const getInterestLevelColor = (level: string) => {
    switch (level) {
      case "high":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading waitlist data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error: {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-500">
        No waitlist data available
      </div>
    );
  }

  const entriesToShow =
    selectedSoftware === "all"
      ? data.all_entries
      : data.entries[selectedSoftware] || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          Software Waitlist Management
        </h2>
        <button
          onClick={fetchWaitlistData}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Refresh Data
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="text-2xl font-bold text-gray-900">
            {data.summary.total_signups}
          </div>
          <div className="text-sm text-gray-600">Total Signups</div>
        </div>
        {data.summary.by_software.map((software) => (
          <div
            key={software.software_type}
            className="bg-white p-6 rounded-lg shadow border"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">
                {getSoftwareIcon(software.software_type)}
              </span>
              <div className="text-lg font-semibold text-gray-900">
                {software.count}
              </div>
            </div>
            <div className="text-sm text-gray-600">{software.name}</div>
            {software.recent_signups_24h > 0 && (
              <div className="text-xs text-green-600 mt-1">
                +{software.recent_signups_24h} in 24h
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setSelectedSoftware("all")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              selectedSoftware === "all"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            All Signups ({data.summary.total_signups})
          </button>
          {data.summary.by_software.map((software) => (
            <button
              key={software.software_type}
              onClick={() => setSelectedSoftware(software.software_type)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedSoftware === software.software_type
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {getSoftwareIcon(software.software_type)} {software.name} (
              {software.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Waitlist Entries Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Software
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interest Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Signup Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entriesToShow.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {entry.name || "Anonymous"}
                      </div>
                      <div className="text-sm text-gray-500">{entry.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">
                        {getSoftwareIcon(entry.software_type)}
                      </span>
                      <span className="text-sm text-gray-900">
                        {data.summary.by_software.find(
                          (s) => s.software_type === entry.software_type
                        )?.name || entry.software_type}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getInterestLevelColor(entry.interest_level)}`}
                    >
                      {entry.interest_level}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(entry.created_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                    {entry.message ? (
                      <div className="truncate" title={entry.message}>
                        {entry.message}
                      </div>
                    ) : (
                      <span className="text-gray-400">No message</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entriesToShow.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No waitlist entries found for the selected software.
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            const csv = [
              "Name,Email,Software,Interest Level,Signup Date,Message",
              ...entriesToShow.map(
                (entry) =>
                  `"${entry.name || ""}","${entry.email}","${entry.software_type}","${entry.interest_level}","${entry.created_at}","${entry.message || ""}"`
              ),
            ].join("\n");

            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `software-waitlist-${selectedSoftware}-${new Date().toISOString().split("T")[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          Export to CSV
        </button>
      </div>
    </div>
  );
};

export default AdminSoftwareWaitlist;
